'use client';

import { doubleLogin } from '@/app/signin/actions';
import { MuiButton } from '@/components/button/MuiButton';
import { MuiTextField } from '@/components/textfield/MuiTextField';
import { createPortalSession } from '@/helpers/accountHelper';
import {
  DoubleLoginResolver,
  DoubleLoginType,
} from '@/schemas/DoubleLoginSchema';
import { PORTAL_USE_TYPE } from '@/types/types';
import { Box, Button, Typography } from '@mui/material';
import { useSession } from 'next-auth/react';
import { useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { LoginWithWebAuthn } from './LoginWithWebAuthn';

export function DoubleLoginForm({
  useType,
  loginInfo,
  setIsLoading,
}: {
  useType: PORTAL_USE_TYPE;
  loginInfo: {
    token: string;
    ewellUserId: number;
  };
  setIsLoading: (value: boolean) => void;
}) {
  const { data: session } = useSession();
  const searchParams = useSearchParams();
  const { setValue, control, handleSubmit, setError } =
    useForm<DoubleLoginType>({
      resolver: DoubleLoginResolver,
      defaultValues: { token: loginInfo.token },
    });

  const onSubmit = async (formValue: DoubleLoginType) => {
    setIsLoading(true);
    const res = await doubleLogin({ formValue });
    if (res && !res.token && res.message) {
      setError('key_code', { type: 'custom', message: res.message });
      setIsLoading(false);
      return;
    }
    const params = new URLSearchParams(searchParams.toString());
    params.set('use_type', useType.toString());
    await createPortalSession(
      searchParams,
      session,
      useType,
      `/recept?${params.toString()}`,
    );
  };

  const handleNotRegistered = () => {
    setError('key_code', {
      type: 'custom',
      message:
        '生体認証情報の登録がないため、送信されたセキュリティコードを入力してください。',
    });
  };

  useEffect(() => {
    setValue('token', loginInfo.token);
  }, [setValue, loginInfo.token]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box display="flex" flexDirection="column" gap={3}>
        <Typography
          variant="JP/Heading/m/Bold/Tight"
          textAlign="center"
          component="p"
        >
          2段階認証
        </Typography>
        <Typography>
          電話番号またはメールアドレスに送られた6桁のセキュリティコードを入力してください。
        </Typography>
        <MuiTextField
          required
          name="key_code"
          control={control}
          label="セキュリティコード"
        />
        <MuiButton type="submit" size="large">
          認証してログインする
        </MuiButton>
        <LoginWithWebAuthn
          useType={useType}
          loginInfo={loginInfo}
          session={session}
          setIsLoading={setIsLoading}
          handleNotRegistered={handleNotRegistered}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            alignItems: 'center',
          }}
        >
          <Button
            type="button"
            variant="text"
            href="https://news.ewellibow.jp/%E3%82%BB%E3%82%AD%E3%83%A5%E3%83%AA%E3%83%86%E3%82%A3%E3%82%B3%E3%83%BC%E3%83%89%E3%81%8C%E5%B1%8A%E3%81%8B%E3%81%AA%E3%81%8B%E3%81%A3%E3%81%9F%E5%A0%B4%E5%90%88/"
            target="_blank"
          >
            セキュリティコードが届かなかった場合
          </Button>
          <Button
            type="button"
            variant="text"
            href="https://news.ewellibow.jp/bio_auth/"
            target="_blank"
          >
            生体認証の詳細はこちら
          </Button>
        </Box>
      </Box>
    </form>
  );
}
